import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import './ActiveBolos.css';
import { saveToGallery, formatSaveMessage } from '../utils/saveToGallery';

const normalizeLower = (value) => (value || '').trim().toLowerCase();

const getIntelPreview = (text = '') => {
  const trimmedText = text.trim();
  if (!trimmedText) return '';

  const lines = trimmedText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length > 1) {
    return lines.slice(0, 2).join(' ');
  }

  const sentences = trimmedText.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
  return sentences.slice(0, 2).map((sentence) => sentence.trim()).join(' ');
};

const getUserAudienceKeys = (user) => {
  const keys = [];
  const countryLower = normalizeLower(user?.countryLower || user?.country);
  const stateProvinceLower = normalizeLower(
    user?.stateProvinceLower || user?.stateProvince || user?.state
  );
  const cityLower = normalizeLower(user?.cityLower || user?.city);

  if (user?.uid) keys.push(`user:${user.uid}`);
  if (countryLower) keys.push(`country:${countryLower}`);
  if (countryLower && stateProvinceLower) {
    keys.push(`state:${countryLower}|${stateProvinceLower}`);
  }
  if (countryLower && stateProvinceLower && cityLower) {
    keys.push(`city:${countryLower}|${stateProvinceLower}|${cityLower}`);
  }

  return keys;
};

const ActiveBolos = () => {
  const auth = getAuth();
  const [intelItems, setIntelItems] = useState([]);
  const [readIntel, setReadIntel] = useState(() => JSON.parse(localStorage.getItem('readIntel')) || []);
  const [currentUser, setCurrentUser] = useState(null);
  const [expandedIntel, setExpandedIntel] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const cachedProfile = localStorage.getItem('currentUserProfile');
    if (cachedProfile) setCurrentUser(JSON.parse(cachedProfile));
  }, []);

  useEffect(() => {
    const userKeys = getUserAudienceKeys({
      ...currentUser,
      uid: currentUser?.uid || auth.currentUser?.uid,
    });

    if (userKeys.length === 0) return undefined;

    const q = query(
      collection(db, 'intel'),
      where('audienceKeys', 'array-contains-any', userKeys.slice(0, 10))
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const active = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
          const expiresAt = data.expiresAt?.toDate?.() || new Date(data.expiresAt);

          return {
            id: docSnap.id,
            ...data,
            createdAt,
            expiresAt: expiresAt.getTime(),
          };
        })
        .filter((intel) => intel.expiresAt > Date.now())
        .sort((a, b) => b.createdAt - a.createdAt);

      setIntelItems(active);
    }, (error) => {
      console.error('Active Intel listener failed:', error);
    });

    return () => unsubscribe();
  }, [auth.currentUser?.uid, currentUser]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIntelItems((prev) => prev.filter((intel) => intel.expiresAt > Date.now()));
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const stateAbbreviations = {
    Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR',
    California: 'CA', Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE',
    Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID',
    Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS',
    Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
    Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS',
    Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
    Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
    Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV',
    Wisconsin: 'WI', Wyoming: 'WY',
  };

  const abbreviateState = (state) => stateAbbreviations[state] || state;

  const parseSenderName = (senderName = '') => {
    const parts = senderName.split(',');
    const rankAndName = (parts[0] || '').trim();
    const senderAgency = (parts[1] || '').trim();
    const senderState = (parts[2] || '').trim();
    const nameParts = rankAndName.split(' ').filter(Boolean);
    const rank = nameParts.slice(0, -1).join(' ');
    const name = nameParts[nameParts.length - 1] || rankAndName;

    return { rank, name, senderAgency, senderState };
  };

  const handleExpand = (id) => {
    setExpandedIntel((prev) =>
      prev.includes(id) ? prev.filter((intelId) => intelId !== id) : [...prev, id]
    );

    if (!readIntel.includes(id)) {
      const updated = [...readIntel, id];
      setReadIntel(updated);
      localStorage.setItem('readIntel', JSON.stringify(updated));
      window.dispatchEvent(new Event('intel-read-updated'));
    }
  };

  const formatRemaining = (expiresAt) => {
    const ms = expiresAt - Date.now();
    const minutes = Math.max(0, Math.floor(ms / 60000));
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${remMinutes}m`;
  };

  return (
    <div className="active-bolos-container">
      {intelItems.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#bbb', marginTop: '20px' }}>
          No active Intel messages.
        </p>
      ) : (
        intelItems.map((intel) => {
          const { rank, name, senderAgency, senderState } = parseSenderName(intel.senderName);
          const isExpanded = expandedIntel.includes(intel.id);
          const intelPreview = getIntelPreview(intel.text);
          return (
            <div
              key={intel.id}
              className="bolo-card intel-card"
              onClick={() => handleExpand(intel.id)}
            >
              {!readIntel.includes(intel.id) && <span className="yellow-dot" />}

              <div className="bolo-title-row">
                <h3 className="bolo-title">
                  <span className="bolo-title-text">{intel.title}</span>
                  {senderAgency && (
                    <>
                      {' - '}
                      <span className="bolo-agency-text">{senderAgency}</span>
                    </>
                  )}
                </h3>
              </div>

              {!isExpanded && (
                <>
                  {intelPreview && (
                    <div className="intel-collapsed-preview">
                      {intelPreview}
                    </div>
                  )}

                  <div className="bolo-footer">
                    <div className="sender-info">
                      <div className="sender-lines">
                        <div className="sender-main">
                          {rank}{' '}
                          <Link
                            to={`/profile/${intel.senderId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="profile-link"
                          >
                            {name}
                          </Link>
                        </div>
                        <div className="sender-agency-state">
                          {[senderAgency, abbreviateState(senderState)].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>

                    <div className="expiration-timer">
                      <div>Expires in:</div>
                      <div className="expires-time">{formatRemaining(intel.expiresAt)}</div>
                    </div>
                  </div>
                </>
              )}

              {isExpanded && (
                <>
                  <div className="bolo-body">
                    <div className="bolo-text" style={{ whiteSpace: 'pre-wrap' }}>
                      {intel.text}
                    </div>

                    {intel.attachedFileUrls?.length > 0 && (
                      <div className="bolo-attachments">
                        {intel.attachedFileUrls.map((url, idx) => (
                          <div key={url} className="bolo-attachment">
                            <img
                              src={url}
                              alt={`attachment-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewImage(url);
                              }}
                              className="bolo-image"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bolo-footer">
                    <div className="sender-info">
                      <div className="sender-lines">
                        <div className="sender-main">
                          {rank}{' '}
                          <Link
                            to={`/profile/${intel.senderId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="profile-link"
                          >
                            {name}
                          </Link>
                        </div>
                        <div className="sender-agency-state">
                          {[senderAgency, abbreviateState(senderState)].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>

                    <div className="expiration-timer">
                      Expires in: {formatRemaining(intel.expiresAt)}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })
      )}

      {previewImage && (
        <div
          className="image-preview-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="image-preview-container"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={previewImage} alt="Preview" className="image-preview-full" />
            <button
              className="download-btn"
              onClick={async () => {
                try {
                  const result = await saveToGallery(previewImage, { fileName: `intel_${Date.now()}.jpg` });
                  alert(formatSaveMessage(result));
                } catch (err) {
                  console.error('Save failed:', err);
                  alert('Error: ' + (err.message || JSON.stringify(err)));
                }
              }}
            >
              Download
            </button>

            <button
              className="close-btn"
              onClick={() => setPreviewImage(null)}
            >
              X
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveBolos;
